'use client'

import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

export interface TooltipProps extends Omit<React.HTMLAttributes<HTMLElement>, 'content'> {
  content: string | ReactNode
  children: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  width?: string
  delayMs?: number
  className?: string
  triggerClassName?: string
  as?: React.ElementType | string
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'top',
  align = 'center',
  width = 'w-64',
  delayMs = 200,
  className,
  triggerClassName,
  as: Component = 'div',
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const [show, setShow] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  // Horizontal nudge applied after measuring the rendered tooltip so it never
  // spills past the viewport edge (e.g. a mention highlighted near the right
  // margin). 0 unless the tooltip would otherwise overflow.
  const [shiftX, setShiftX] = useState(0)
  const [isReady, setIsReady] = useState(false)
  const triggerRef = useRef<any>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const scrollX = window.scrollX
    const scrollY = window.scrollY
    const gap = 8

    let top = 0
    let left = 0

    switch (side) {
      case 'top':
        top = rect.top + scrollY - gap
        break
      case 'bottom':
        top = rect.bottom + scrollY + gap
        break
      case 'left':
        top = rect.top + scrollY + rect.height / 2
        left = rect.left + scrollX - gap
        break
      case 'right':
        top = rect.top + scrollY + rect.height / 2
        left = rect.right + scrollX + gap
        break
    }

    if (side === 'top' || side === 'bottom') {
      switch (align) {
        case 'start':
          left = rect.left + scrollX
          break
        case 'center':
          left = rect.left + scrollX + rect.width / 2
          break
        case 'end':
          left = rect.right + scrollX
          break
      }
    }

    setPosition({ top, left })
    setIsReady(true)
  }, [side, align])

  useEffect(() => {
    if (show) {
      updatePosition()
    } else {
      setIsReady(false)
      setShiftX(0)
    }
  }, [show, updatePosition])

  // Clamp into the viewport horizontally. Measuring the rendered box keeps this
  // transform-agnostic (the box already carries the side/align translate), so a
  // single left nudge brings it back in-bounds. Converges in one pass: once the
  // box fits, the computed delta is 0 and no further state update fires.
  useEffect(() => {
    if (!show || !isReady || !tooltipRef.current) return
    const rect = tooltipRef.current.getBoundingClientRect()
    const margin = 8
    const vw = document.documentElement.clientWidth
    let dx = 0
    if (rect.left < margin) dx = margin - rect.left
    else if (rect.right > vw - margin) dx = vw - margin - rect.right
    if (Math.abs(dx) > 1) setShiftX((s) => s + dx)
  }, [show, isReady, position, shiftX])

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => setShow(true), delayMs)
  }

  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current)
    setShow(false)
  }

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  const transformMap = {
    top: align === 'center' ? '-translate-x-1/2 -translate-y-full' : align === 'end' ? '-translate-x-full -translate-y-full' : '-translate-y-full',
    bottom: align === 'center' ? '-translate-x-1/2' : align === 'end' ? '-translate-x-full' : '',
    left: '-translate-x-full -translate-y-1/2',
    right: '-translate-y-1/2',
  }

  const arrowMap = {
    top: 'bottom-[-5px] left-1/2 -translate-x-1/2 rotate-45',
    bottom: 'top-[-5px] left-1/2 -translate-x-1/2 rotate-45',
    left: 'right-[-5px] top-1/2 -translate-y-1/2 rotate-45',
    right: 'left-[-5px] top-1/2 -translate-y-1/2 rotate-45',
  }

  return (
    <Component
      className={Component === 'div' ? cn('inline-flex', triggerClassName) : triggerClassName}
      onMouseEnter={(e: any) => {
        handleMouseEnter()
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e: any) => {
        handleMouseLeave()
        onMouseLeave?.(e)
      }}
      ref={triggerRef}
      {...props}
    >
      {children}

      {show && typeof document !== 'undefined' && createPortal(
        <div
          ref={tooltipRef}
          className={cn(
            'absolute z-[9999] p-3 rounded-lg shadow-xl animate-in fade-in duration-200 pointer-events-none transition-opacity',
            width,
            transformMap[side],
            isReady ? 'opacity-100' : 'opacity-0',
            className
          )}
          style={{
            top: position.top,
            left: position.left + shiftX,
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-border)',
          }}
        >
          <div className="text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
            {content}
          </div>
          <div
            className={cn('absolute w-2 h-2', arrowMap[side])}
            style={{
              // Keep the arrow pointing at the trigger after a horizontal clamp.
              marginLeft: (side === 'top' || side === 'bottom') ? -shiftX : undefined,
              background: 'var(--color-surface-elevated)',
              borderRight: (side === 'top' || side === 'left') ? '1px solid var(--color-border)' : undefined,
              borderBottom: (side === 'top' || side === 'left') ? '1px solid var(--color-border)' : undefined,
              borderLeft: (side === 'bottom' || side === 'right') ? '1px solid var(--color-border)' : undefined,
              borderTop: (side === 'bottom' || side === 'right') ? '1px solid var(--color-border)' : undefined,
            }}
          />
        </div>,
        document.body
      )}
    </Component>
  )
}

export { Tooltip }
